import React, { useState, useCallback, useRef } from 'react';
import { Icon, Text, Tooltip, useTheme } from '@Sage/hoi-poi-ui';
import { getLiteral } from 'utils/getLiteral';
import { setDefaultSender, deleteSender } from 'services/SenderService';
import { successToast, errorToast } from 'utils/toast';
import ConfirmModal from 'components/ConfirmModal';

function clearActionsFocus(container) {
    const active = document.activeElement;
    if (active instanceof HTMLElement && container?.contains(active)) {
        active.blur();
    }
}

function isMakeDefaultAllowed(data) {
    if (data?.default) {
        return false;
    }
    const verified = data?.domainVerified ?? data?.isDomainVerified ?? data?.isSenderDomainVerified;
    if (verified === undefined) {
        return true;
    }
    return Boolean(verified);
}

const SenderActionsCell = (params) => {
    const theme = useTheme();
    const { data, senders, onChangeDefault, onDeleteSender, onClickEditSender } = params;
    const cellRef = useRef(null);
    const [isSettingDefault, setIsSettingDefault] = useState(false);
    const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
    const [isDeleting, setIsDeleting] = useState(false);

    const handleMakeDefault = useCallback(
        (senderId) => {
            setIsSettingDefault(true);

            setDefaultSender(senderId)
                .then(() => {
                    successToast({
                        text: getLiteral('succes_entityupdatedsuccessfully'),
                    });
                    onChangeDefault?.();
                })
                .catch(() => {
                    errorToast({
                        text: getLiteral('label_failed_update'),
                    });
                })
                .finally(() => {
                    setIsSettingDefault(false);
                });
        },
        [onChangeDefault],
    );

    const openDeleteModal = useCallback(() => {
        clearActionsFocus(cellRef.current);
        setIsDeleteModalOpen(true);
    }, []);

    const closeDeleteModal = useCallback(() => {
        if (isDeleting) return;
        clearActionsFocus(cellRef.current);
        setIsDeleteModalOpen(false);
    }, [isDeleting]);

    const handleEditSender = useCallback(() => {
        clearActionsFocus(cellRef.current);
        onClickEditSender?.(data);
    }, [data, onClickEditSender]);

    const handleActionsMouseLeave = useCallback(() => {
        clearActionsFocus(cellRef.current);
    }, []);

    const handleConfirmDelete = useCallback(() => {
        if (isDeleting) return;

        setIsDeleting(true);

        deleteSender(data.id)
            .then(() => {
                successToast({
                    text: getLiteral('succes_entitydeletedsuccessfully'),
                });
                closeDeleteModal();
                onDeleteSender?.();
            })
            .catch(() => {
                errorToast({
                    text: getLiteral('error_generalerror'),
                });
            })
            .finally(() => {
                setIsDeleting(false);
            });
    }, [data.id, isDeleting, onDeleteSender, closeDeleteModal]);

    const showMakeDefault = isMakeDefaultAllowed(data);
    const showDelete = senders.length > 1 && !data.default;
    const iconColor = theme.colors.actionMinor[500];

    const actionKeyDown = (handler) => (e) => {
        if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            handler();
        }
    };

    return (
        <div className="sender-actions-cell" ref={cellRef} onMouseLeave={handleActionsMouseLeave}>
            <div className="sender-actions-cell__toolbar">
                {showMakeDefault ? (
                    <Tooltip
                        content={getLiteral('label_marketing_campaigns_make_default_sender')}
                        placement="top"
                    >
                        <span
                            className="sender-actions-cell__action-btn"
                            role="button"
                            tabIndex={isSettingDefault ? -1 : 0}
                            aria-disabled={isSettingDefault}
                            onClick={() => {
                                if (isSettingDefault) return;
                                clearActionsFocus(cellRef.current);
                                handleMakeDefault(data.id);
                            }}
                            onKeyDown={actionKeyDown(
                                () => !isSettingDefault && handleMakeDefault(data.id),
                            )}
                        >
                            <Icon name="defaultSender" size="large" color={iconColor} />
                        </span>
                    </Tooltip>
                ) : null}
                <Tooltip content={getLiteral('action_edit')} placement="top">
                    <span
                        className="sender-actions-cell__action-btn"
                        role="button"
                        tabIndex={0}
                        onClick={handleEditSender}
                        onKeyDown={actionKeyDown(handleEditSender)}
                    >
                        <Icon name="edit" size="large" color={iconColor} />
                    </span>
                </Tooltip>
                {showDelete ? (
                    <Tooltip content={getLiteral('action_delete')} placement="top">
                        <span
                            className="sender-actions-cell__action-btn"
                            role="button"
                            tabIndex={0}
                            onClick={openDeleteModal}
                            onKeyDown={actionKeyDown(openDeleteModal)}
                        >
                            <Icon name="delete" size="large" color={iconColor} />
                        </span>
                    </Tooltip>
                ) : null}
            </div>
            <ConfirmModal
                isOpen={isDeleteModalOpen}
                onClose={closeDeleteModal}
                onConfirm={handleConfirmDelete}
                title={getLiteral('title_marketing_campaigns_delete_sender')}
                confirmText={getLiteral('action_delete')}
                cancelText={getLiteral('action_cancel')}
                isLoading={isDeleting}
                buttonType="primary-error"
                size="small"
            >
                <Text>{getLiteral('label_marketing_campaigns_delete_sender_message')}</Text>
            </ConfirmModal>
        </div>
    );
};

SenderActionsCell.displayName = 'SenderActionsCell';

export default SenderActionsCell;
